export interface MenuNode {
  menuIdx: number;
  name: string;
  path?: string;
  icon?: string;
  orderNo?: number;
  vendorCategoryIdx?: number | null;
  parentMenuIdx?: number | null;
  isActive?: string | boolean;
  isGuest?: string | boolean;
  children?: MenuNode[];
}

export type MenuContextType = "OWNER" | "PARTNER";

export interface MenuContextNode {
  contextType: MenuContextType;
  hostVendorIdx: number | null;
  hostVendorName: string;
  isGuestContext: boolean;
  menus: MenuNode[];
  contextKey: string;
}

const LEGACY_VENDOR_CATEGORY_MAP: Record<number, number> = {
  89: 9,
  90: 9,
};

const toFiniteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const pickNonEmptyString = (...values: unknown[]) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
};

export const normalizeBoolean = (value: unknown): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const lowered = value.trim().toLowerCase();
    return lowered === "y" || lowered === "yes" || lowered === "true" || lowered === "1";
  }
  if (typeof value === "number") return value === 1;
  return false;
};

export const normalizeVendorCategoryIdx = (value: unknown): number | null => {
  const parsed = toFiniteNumber(value);
  if (parsed === null) return null;
  return LEGACY_VENDOR_CATEGORY_MAP[parsed] ?? parsed;
};

export const getUserVendorCategoryIdx = (user: any): number | null => {
  return normalizeVendorCategoryIdx(
    user?.vendorCategoryIdx ?? user?.vendor?.category?.vendorCategoryIdx ?? null
  );
};

export const isGuestVendorUser = (user: any): boolean => {
  if (
    normalizeBoolean(user?.isGuest) ||
    normalizeBoolean(user?.guest) ||
    normalizeBoolean(user?.isGuestVendor)
  ) {
    return true;
  }

  const vendorType = String(
    user?.vendorType ?? user?.type ?? user?.vendor?.vendorType ?? user?.vendor?.type ?? ""
  )
    .trim()
    .toUpperCase();

  return vendorType.includes("GUEST");
};

const normalizeContextType = (value: unknown): MenuContextType | null => {
  const upper = String(value ?? "")
    .trim()
    .toUpperCase();
  if (upper === "OWNER") return "OWNER";
  if (upper === "PARTNER") return "PARTNER";
  return null;
};

export const buildMenuContextKey = (context: {
  contextType: MenuContextType;
  hostVendorIdx: number | null;
  isGuestContext: boolean;
}) => {
  const hostVendorKey = context.hostVendorIdx === null ? "none" : String(context.hostVendorIdx);
  const guestKey = context.isGuestContext ? "guest" : "owner";
  return `${context.contextType}:${hostVendorKey}:${guestKey}`;
};

const normalizeMenuContext = (raw: any): MenuContextNode | null => {
  const resolvedContextType =
    normalizeContextType(raw?.contextType) ??
    (normalizeBoolean(raw?.isGuestContext) ? "PARTNER" : "OWNER");
  const hostVendorIdx = toFiniteNumber(raw?.hostVendorIdx ?? raw?.vendorIdx ?? raw?.hostVendor?.vendorIdx);
  const hostVendorName =
    pickNonEmptyString(
      raw?.hostVendorName,
      raw?.vendorName,
      raw?.hostVendor?.name,
      resolvedContextType === "OWNER" ? "내 업체" : "파트너 업체"
    ) || (resolvedContextType === "OWNER" ? "내 업체" : "파트너 업체");
  // contextType 이 OWNER/PARTNER로 내려오는 신규 스펙에서는 contextType을 최우선으로 신뢰한다.
  // (OWNER + isGuestContext=true 같은 모순 데이터가 와도 OWNER로 고정)
  const isGuestContext = resolvedContextType === "PARTNER";
  const menus = normalizeMenuItems(Array.isArray(raw?.menus) ? raw.menus : []);

  return {
    contextType: resolvedContextType,
    hostVendorIdx,
    hostVendorName,
    isGuestContext,
    menus,
    contextKey: buildMenuContextKey({
      contextType: resolvedContextType,
      hostVendorIdx,
      isGuestContext,
    }),
  };
};

const dedupeMenuContexts = (contexts: MenuContextNode[]) => {
  const map = new Map<string, MenuContextNode>();
  for (const context of contexts) {
    const existing = map.get(context.contextKey);
    if (!existing) {
      map.set(context.contextKey, context);
      continue;
    }

    if (context.menus.length > existing.menus.length) {
      map.set(context.contextKey, context);
    }
  }

  return Array.from(map.values()).sort((a, b) => {
    if (a.contextType !== b.contextType) {
      return a.contextType === "OWNER" ? -1 : 1;
    }
    const hostVendorDiff = (a.hostVendorIdx ?? Number.MAX_SAFE_INTEGER) - (b.hostVendorIdx ?? Number.MAX_SAFE_INTEGER);
    if (hostVendorDiff !== 0) return hostVendorDiff;
    return a.hostVendorName.localeCompare(b.hostVendorName, "ko");
  });
};

export const normalizeMenuContexts = (user: any): MenuContextNode[] => {
  const ownerMenus = normalizeMenuItems(Array.isArray(user?.accessibleMenus) ? user.accessibleMenus : []);
  const guestMenus = normalizeMenuItems(
    Array.isArray(user?.accessibleGuestMenus) ? user.accessibleGuestMenus : []
  );

  const fromApi = Array.isArray(user?.menuContexts)
    ? user.menuContexts
        .map((context: any) => normalizeMenuContext(context))
        .filter((context: MenuContextNode | null): context is MenuContextNode => Boolean(context))
    : [];

  if (fromApi.length > 0) {
    const normalizedFromApi = fromApi.map((context) => {
      const normalizedIsGuest = context.contextType === "PARTNER";
      const fallbackMenus =
        context.contextType === "OWNER"
          ? (ownerMenus.length > 0 ? ownerMenus : context.menus)
          : (guestMenus.length > 0 ? guestMenus : context.menus);
      const menus = context.menus.length > 0 ? context.menus : fallbackMenus;

      return {
        ...context,
        isGuestContext: normalizedIsGuest,
        menus,
        contextKey: buildMenuContextKey({
          contextType: context.contextType,
          hostVendorIdx: context.hostVendorIdx,
          isGuestContext: normalizedIsGuest,
        }),
      };
    });

    const supplementalContexts: MenuContextNode[] = [];
    const hasOwnerContext = normalizedFromApi.some((context) => context.contextType === "OWNER");
    const hasPartnerContext = normalizedFromApi.some((context) => context.contextType === "PARTNER");

    if (!hasOwnerContext && ownerMenus.length > 0) {
      const ownerHostVendorIdx = toFiniteNumber(user?.vendorIdx ?? user?.ownerVendorIdx);
      const ownerHostVendorName = pickNonEmptyString(user?.vendorName, user?.name, "내 업체") || "내 업체";
      supplementalContexts.push({
        contextType: "OWNER",
        hostVendorIdx: ownerHostVendorIdx,
        hostVendorName: ownerHostVendorName,
        isGuestContext: false,
        menus: ownerMenus,
        contextKey: buildMenuContextKey({
          contextType: "OWNER",
          hostVendorIdx: ownerHostVendorIdx,
          isGuestContext: false,
        }),
      });
    }

    if (!hasPartnerContext && guestMenus.length > 0) {
      const partnerHostVendorIdx = toFiniteNumber(user?.hostVendorIdx ?? user?.ownerVendorIdx);
      const partnerHostVendorName = pickNonEmptyString(user?.hostVendorName, user?.ownerVendorName, "파트너 업체");
      supplementalContexts.push({
        contextType: "PARTNER",
        hostVendorIdx: partnerHostVendorIdx,
        hostVendorName: partnerHostVendorName || "파트너 업체",
        isGuestContext: true,
        menus: guestMenus,
        contextKey: buildMenuContextKey({
          contextType: "PARTNER",
          hostVendorIdx: partnerHostVendorIdx,
          isGuestContext: true,
        }),
      });
    }

    return dedupeMenuContexts([...normalizedFromApi, ...supplementalContexts]);
  }

  const fallback: MenuContextNode[] = [];
  if (ownerMenus.length > 0 || guestMenus.length === 0) {
    const ownerHostVendorIdx = toFiniteNumber(user?.vendorIdx ?? user?.ownerVendorIdx);
    const ownerHostVendorName = pickNonEmptyString(user?.vendorName, user?.name, "내 업체") || "내 업체";
    fallback.push({
      contextType: "OWNER",
      hostVendorIdx: ownerHostVendorIdx,
      hostVendorName: ownerHostVendorName,
      isGuestContext: false,
      menus: ownerMenus,
      contextKey: buildMenuContextKey({
        contextType: "OWNER",
        hostVendorIdx: ownerHostVendorIdx,
        isGuestContext: false,
      }),
    });
  }

  if (guestMenus.length > 0) {
    const partnerHostVendorIdx = toFiniteNumber(user?.hostVendorIdx ?? user?.ownerVendorIdx);
    const partnerHostVendorName = pickNonEmptyString(user?.hostVendorName, user?.ownerVendorName, "파트너 업체");
    fallback.push({
      contextType: "PARTNER",
      hostVendorIdx: partnerHostVendorIdx,
      hostVendorName: partnerHostVendorName || "파트너 업체",
      isGuestContext: true,
      menus: guestMenus,
      contextKey: buildMenuContextKey({
        contextType: "PARTNER",
        hostVendorIdx: partnerHostVendorIdx,
        isGuestContext: true,
      }),
    });
  }

  return dedupeMenuContexts(fallback);
};

export const getDefaultMenuContext = (contexts: MenuContextNode[]): MenuContextNode | null => {
  if (!Array.isArray(contexts) || contexts.length === 0) return null;
  return contexts.find((context) => context.contextType === "OWNER") ?? contexts[0];
};

export const getSelectedMenuContext = (user: any): MenuContextNode | null => {
  const contexts = normalizeMenuContexts(user);
  if (contexts.length === 0) return null;

  const selectedKey =
    typeof user?.selectedMenuContextKey === "string" && user.selectedMenuContextKey.trim()
      ? user.selectedMenuContextKey.trim()
      : "";
  if (selectedKey) {
    const matched = contexts.find((context) => context.contextKey === selectedKey);
    if (matched) return matched;
  }

  const selectedContextType = normalizeContextType(user?.selectedContextType);
  const selectedHostVendorIdx = toFiniteNumber(user?.selectedContextHostVendorIdx);
  if (selectedContextType) {
    const inferredKey = buildMenuContextKey({
      contextType: selectedContextType,
      hostVendorIdx: selectedHostVendorIdx,
      isGuestContext: selectedContextType === "PARTNER",
    });
    const matched = contexts.find((context) => context.contextKey === inferredKey);
    if (matched) return matched;
  }

  return getDefaultMenuContext(contexts);
};

export const upsertSelectedMenuContext = (user: any, selectedContext: MenuContextNode) => {
  const contexts = normalizeMenuContexts(user);
  const contextKey = selectedContext.contextKey || buildMenuContextKey(selectedContext);

  return {
    ...user,
    menuContexts: contexts,
    selectedMenuContextKey: contextKey,
    selectedContextType: selectedContext.contextType,
    selectedContextHostVendorIdx: selectedContext.hostVendorIdx,
    selectedContextIsGuest: selectedContext.isGuestContext,
  };
};

export const buildMenuQueryParams = (params: {
  vendorCategoryIdx?: number | null;
  isGuest?: boolean;
  isActive?: boolean;
  includeInactive?: boolean;
  name?: string;
  path?: string;
  icon?: string;
  orderNo?: number;
  parentMenuIdx?: number;
}) => {
  const qs = new URLSearchParams();

  if (params.vendorCategoryIdx !== undefined && params.vendorCategoryIdx !== null) {
    qs.set("vendorCategoryIdx", String(params.vendorCategoryIdx));
  }
  if (params.isGuest !== undefined) qs.set("isGuest", String(params.isGuest));
  if (params.isActive !== undefined) qs.set("isActive", String(params.isActive));
  if (params.includeInactive !== undefined) qs.set("includeInactive", String(params.includeInactive));
  if (params.name) qs.set("name", params.name);
  if (params.path) qs.set("path", params.path);
  if (params.icon) qs.set("icon", params.icon);
  if (params.orderNo !== undefined) qs.set("orderNo", String(params.orderNo));
  if (params.parentMenuIdx !== undefined) qs.set("parentMenuIdx", String(params.parentMenuIdx));

  const str = qs.toString();
  return str ? `?${str}` : "";
};

export const extractMenuArray = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.menus)) return payload.menus;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.data?.menus)) return payload.data.menus;
  return [];
};

export const normalizeMenuItems = (input: any[]): MenuNode[] => {
  const flattened: MenuNode[] = [];
  let syntheticMenuIdx = -1;

  const pickString = (...values: unknown[]) => {
    for (const value of values) {
      if (typeof value === "string" && value.trim()) return value.trim();
    }
    return "";
  };

  const walk = (nodes: any[], inheritedParent: number | null = null) => {
    for (const raw of nodes) {
      const menuIdx =
        toFiniteNumber(raw?.menuIdx ?? raw?.id ?? raw?.menuId ?? raw?.idx) ?? syntheticMenuIdx--;
      const name = pickString(raw?.name, raw?.label, raw?.title);
      if (!name) continue;

      const parentMenuIdx = toFiniteNumber(
        raw?.parentMenuIdx ?? raw?.parentId ?? raw?.parent_menu_idx ?? raw?.groupId
      );
      const resolvedParent = parentMenuIdx ?? inheritedParent;

      flattened.push({
        menuIdx,
        name,
        path: pickString(raw?.path, raw?.url, raw?.routePath),
        icon: pickString(raw?.icon, raw?.iconClass, raw?.iconName),
        orderNo: toFiniteNumber(raw?.orderNo ?? raw?.order ?? raw?.seq) ?? 0,
        vendorCategoryIdx: normalizeVendorCategoryIdx(
          raw?.vendorCategoryIdx ?? raw?.categoryIdx ?? raw?.vendor_category_idx
        ),
        parentMenuIdx: resolvedParent,
        isActive: raw?.isActive,
        isGuest: raw?.isGuest,
      });

      const children = Array.isArray(raw?.children)
        ? raw.children
        : Array.isArray(raw?.items)
          ? raw.items
          : Array.isArray(raw?.subMenus)
            ? raw.subMenus
            : [];
      if (children.length > 0) {
        walk(children, menuIdx);
      }
    }
  };

  walk(input);

  const deduped = new Map<string, MenuNode>();
  const score = (menu: MenuNode) => (menu.vendorCategoryIdx === null || menu.vendorCategoryIdx === undefined ? 0 : 1);

  for (const item of flattened) {
    const key = item.path ? `path:${item.path}` : `name:${item.name}|parent:${item.parentMenuIdx ?? "root"}`;
    const existing = deduped.get(key);
    if (!existing) {
      deduped.set(key, item);
      continue;
    }
    if (score(item) > score(existing)) {
      deduped.set(key, item);
      continue;
    }
    if ((item.orderNo ?? Number.MAX_SAFE_INTEGER) < (existing.orderNo ?? Number.MAX_SAFE_INTEGER)) {
      deduped.set(key, item);
    }
  }

  return Array.from(deduped.values()).sort((a, b) => {
    const orderDiff = (a.orderNo ?? Number.MAX_SAFE_INTEGER) - (b.orderNo ?? Number.MAX_SAFE_INTEGER);
    if (orderDiff !== 0) return orderDiff;
    return a.menuIdx - b.menuIdx;
  });
};

export const buildMenuTree = (items: MenuNode[]): MenuNode[] => {
  const map = new Map<number, MenuNode>();
  const roots: MenuNode[] = [];

  for (const item of items) {
    map.set(item.menuIdx, { ...item, children: [] });
  }

  for (const item of map.values()) {
    if (item.parentMenuIdx && map.has(item.parentMenuIdx)) {
      map.get(item.parentMenuIdx)!.children!.push(item);
    } else {
      roots.push(item);
    }
  }

  const sortTree = (nodes: MenuNode[]) => {
    nodes.sort((a, b) => {
      const orderDiff = (a.orderNo ?? Number.MAX_SAFE_INTEGER) - (b.orderNo ?? Number.MAX_SAFE_INTEGER);
      if (orderDiff !== 0) return orderDiff;
      return a.menuIdx - b.menuIdx;
    });
    nodes.forEach((node) => sortTree(node.children || []));
  };

  sortTree(roots);
  return roots;
};
